import React, { useState, useEffect } from 'react';
import { X, Target, PieChart, TrendingUp, ArrowRight, Sparkles, Users } from 'lucide-react';

interface GuidancePopupProps {
  isVisible: boolean;
  onClose: () => void;
  onSelectProductSimulation: () => void;
  onSelectStrategySimulation: () => void;
  onSelectNeedsAnalysis: () => void;
}

const GuidancePopup: React.FC<GuidancePopupProps> = ({
  isVisible,
  onClose,
  onSelectProductSimulation,
  onSelectStrategySimulation,
  onSelectNeedsAnalysis
}) => {
  const [step, setStep] = useState<'welcome' | 'choice'>('welcome');
  const [animationClass, setAnimationClass] = useState('');

  useEffect(() => {
    if (isVisible) {
      setStep('welcome');
      setAnimationClass('animate-in fade-in-0 slide-in-from-bottom-4 duration-500');
    }
  }, [isVisible]);

  const handleNextStep = () => {
    setAnimationClass('animate-out fade-out-0 slide-out-to-left-4 duration-300');
    setTimeout(() => {
      setStep('choice');
      setAnimationClass('animate-in fade-in-0 slide-in-from-right-4 duration-500');
    }, 300);
  };

  const handleProductChoice = () => {
    onSelectProductSimulation();
    onClose();
  };

  const handleStrategyChoice = () => {
    onSelectStrategySimulation();
    onClose();
  };

  const handleNeedsChoice = () => {
    onSelectNeedsAnalysis();
    onClose();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998] flex items-center justify-center p-4">
        {/* Popup Container */}
        <div className={`bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 relative overflow-hidden ${animationClass}`}>
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          {step === 'welcome' && (
            <div className="p-6 text-center">
              {/* Header with Icon */}
              <div className="mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Bienvenue chez SOeasi ! 👋
                </h2>
                <p className="text-gray-600 text-xs leading-relaxed">
                  Nous sommes là pour vous accompagner dans votre projet d'investissement. 
                  Laissez-nous vous guider vers la solution qui vous correspond le mieux.
                </p>
              </div>

              {/* Features */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center text-left text-xs text-gray-700">
                  <Sparkles className="w-4 h-4 text-yellow-500 mr-2 flex-shrink-0" />
                  <span>Simulations personnalisées et interactives</span>
                </div>
                <div className="flex items-center text-left text-xs text-gray-700">
                  <Target className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  <span>Conseils adaptés à votre profil d'investisseur</span>
                </div>
                <div className="flex items-center text-left text-xs text-gray-700">
                  <TrendingUp className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                  <span>Analyse complète de vos objectifs financiers</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleNextStep}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
              >
                <span>Commencer mon analyse</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 'choice' && (
            <div className="p-5">
              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  Choisissez votre approche 
                </h2>
                <p className="text-gray-600 text-xs">
                  Quelle méthode préférez-vous pour explorer nos solutions ?
                </p>
              </div>

              {/* Choice Cards */}
              <div className="space-y-3">
                {/* Product Simulation */}
                <button
                  onClick={handleProductChoice}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-teal-400 hover:bg-teal-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
                      <img 
                        src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop&crop=center"
                        alt="Projets Sukuk - Finance éthique"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900 mb-1">
                        Simulateur de Produits
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Explorez <strong>SCPI, GFI et Sukuk</strong>. 
                        Comparez leurs caractéristiques.
                      </p>
                      <div className="flex items-center mt-2 text-teal-600 text-xs font-semibold">
                        <span>Découvrir</span>
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </div>
                  </div>
                </button>

                {/* Strategy Simulation */}
                <button
                  onClick={handleStrategyChoice}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-teal-400 hover:bg-teal-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
                      <img 
                        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop&crop=center"
                        alt="Stratégies d'investissement - Immobilier professionnel"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900 mb-1">
                        Simulateur de Stratégies
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Choisissez votre niveau de risque : <strong>Protecteur, Équilibré ou Dynamique</strong>.
                      </p>
                      <div className="flex items-center mt-2 text-teal-600 text-xs font-semibold">
                        <span>Choisir ma stratégie</span>
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </div>
                  </div>
                </button>

                {/* AI Needs Analysis */}
                <button
                  onClick={handleNeedsChoice}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-teal-400 hover:bg-teal-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
                      <img 
                        src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop&crop=center"
                        alt="Intelligence Artificielle - Analyse personnalisée"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900 mb-1">
                        Analyse IA Personnalisée
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Notre IA analyse vos <strong>besoins spécifiques</strong> pour une stratégie 100% sur mesure.
                      </p>
                      <div className="flex items-center mt-2 text-teal-600 text-xs font-semibold">
                        <span>Commencer l'analyse IA</span>
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Footer Note */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  💡 Vous pourrez toujours explorer les autres approches plus tard
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GuidancePopup;